<template>
    <app-layout>
        <template #title>Tools</template>
        <template #description>Admin utilities</template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6 mx-auto">
            <!-- Bulk Invoice Download -->
            <div
                class="bg-white rounded-xl border-iron-200 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <div class="flex items-start justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-iron-900">
                                {{ $t("kiosk.bulk_invoice_download.title") }}
                            </h3>
                            <p class="text-iron-400 mt-0.5 text-[12px]">
                                {{
                                    $t(
                                        "kiosk.bulk_invoice_download.description",
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                    <div class="mt-3 gap-3 flex items-end">
                        <div class="w-40">
                            <label
                                for="start-date"
                                class="font-medium text-iron-400 tracking-wider mb-1 block text-[11px] uppercase"
                            >
                                {{
                                    $t("kiosk.bulk_invoice_download.start_date")
                                }}
                            </label>
                            <Input
                                id="start-date"
                                v-model="invoiceForm.start_date"
                                type="date"
                                class="text-sm"
                            />
                        </div>
                        <div class="w-40">
                            <label
                                for="end-date"
                                class="font-medium text-iron-400 tracking-wider mb-1 block text-[11px] uppercase"
                            >
                                {{ $t("kiosk.bulk_invoice_download.end_date") }}
                            </label>
                            <Input
                                id="end-date"
                                v-model="invoiceForm.end_date"
                                type="date"
                                class="text-sm"
                            />
                        </div>
                        <Button
                            size="sm"
                            @click="downloadInvoices"
                            :disabled="
                                invoiceForm.processing ||
                                !invoiceForm.start_date ||
                                !invoiceForm.end_date
                            "
                            class="gap-1.5"
                        >
                            <Download class="h-3 w-3" />
                            {{
                                invoiceForm.processing
                                    ? $t(
                                          "kiosk.bulk_invoice_download.downloading",
                                      )
                                    : $t(
                                          "kiosk.bulk_invoice_download.download_button",
                                      )
                            }}
                        </Button>
                    </div>
                </div>
            </div>

            <!-- FlexiBee XML Export -->
            <div
                class="bg-white rounded-xl border-iron-200 overflow-hidden border"
            >
                <div class="px-5 py-4">
                    <div class="flex items-start justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-iron-900">
                                {{ $t("kiosk.flexibee_xml_export.title") }}
                            </h3>
                            <p class="text-iron-400 mt-0.5 text-[12px]">
                                {{
                                    $t("kiosk.flexibee_xml_export.description")
                                }}
                            </p>
                        </div>
                    </div>
                    <div class="mt-3 gap-3 flex flex-wrap items-end">
                        <div class="w-32">
                            <label
                                for="flexibee-month"
                                class="font-medium text-iron-400 tracking-wider mb-1 block text-[11px] uppercase"
                            >
                                {{ $t("kiosk.flexibee_xml_export.month") }}
                            </label>
                            <select
                                id="flexibee-month"
                                v-model="flexibeeForm.month"
                                class="border-iron-200 rounded-md px-3 py-2 text-sm w-full border"
                            >
                                <option v-for="m in 12" :key="m" :value="m">
                                    {{ String(m).padStart(2, "0") }}
                                </option>
                            </select>
                        </div>
                        <div class="w-28">
                            <label
                                for="flexibee-year"
                                class="font-medium text-iron-400 tracking-wider mb-1 block text-[11px] uppercase"
                            >
                                {{ $t("kiosk.flexibee_xml_export.year") }}
                            </label>
                            <Input
                                id="flexibee-year"
                                v-model.number="flexibeeForm.year"
                                type="number"
                                class="text-sm"
                            />
                        </div>
                        <Button
                            size="sm"
                            @click="downloadFlexibeeXml"
                            :disabled="flexibeeForm.processing"
                            class="gap-1.5"
                        >
                            <Download class="h-3 w-3" />
                            {{
                                flexibeeForm.processing
                                    ? $t(
                                          "kiosk.flexibee_xml_export.downloading",
                                      )
                                    : $t(
                                          "kiosk.flexibee_xml_export.download_button",
                                      )
                            }}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Download } from "lucide-vue-next";
import { useToast } from "@/components/ui/toast/use-toast";
import axios from "axios";
import route from "ziggy-js";

const { toast } = useToast();
const invoiceForm = ref({
    start_date: "",
    end_date: "",
    processing: false,
});

const now = new Date();
const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const flexibeeForm = ref({
    month: previousMonthDate.getMonth() + 1,
    year: previousMonthDate.getFullYear(),
    processing: false,
});

async function downloadInvoices() {
    if (!invoiceForm.value.start_date || !invoiceForm.value.end_date) {
        toast({
            title: "Error",
            description: "Please select both start and end dates",
            variant: "destructive",
        });
        return;
    }

    invoiceForm.value.processing = true;

    try {
        const response = await axios.post(
            route("app.kiosk.invoices.bulk-download"),
            {
                start_date: invoiceForm.value.start_date,
                end_date: invoiceForm.value.end_date,
            },
            { responseType: "blob" },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `zaptime-invoices-${invoiceForm.value.start_date}-to-${invoiceForm.value.end_date}.zip`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
            title: "Success",
            description: "Invoices downloaded successfully",
        });
    } catch (error: any) {
        console.error("Error downloading invoices:", error);
        toast({
            title: "Error",
            description:
                error.response?.data?.message || "Error downloading invoices",
            variant: "destructive",
        });
    } finally {
        invoiceForm.value.processing = false;
    }
}

async function downloadFlexibeeXml() {
    flexibeeForm.value.processing = true;

    try {
        const response = await axios.post(
            route("app.kiosk.invoices.flexibee-xml"),
            {
                month: flexibeeForm.value.month,
                year: flexibeeForm.value.year,
            },
            { responseType: "blob" },
        );

        const url = window.URL.createObjectURL(
            new Blob([response.data], { type: "application/xml" }),
        );
        const link = document.createElement("a");
        link.href = url;
        const filename = `faktury-${flexibeeForm.value.year}-${String(flexibeeForm.value.month).padStart(2, "0")}.xml`;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
            title: "Success",
            description: "FlexiBee XML downloaded successfully",
        });
    } catch (error: any) {
        console.error("Error downloading FlexiBee XML:", error);

        let description = "Error downloading FlexiBee XML";
        if (error.response?.data instanceof Blob) {
            try {
                description = await error.response.data.text();
            } catch {
                // ignore
            }
        } else if (error.response?.data?.message) {
            description = error.response.data.message;
        }

        toast({
            title: "Error",
            description,
            variant: "destructive",
        });
    } finally {
        flexibeeForm.value.processing = false;
    }
}
</script>
